import {ReactNode, useRef, useState, UIEvent, useEffect, useCallback} from 'react';
import styles from './style.module.scss';
import cs from 'classnames';
import _throttle from 'lodash/throttle';

interface IHorizontalScrollContainerProps {
    className?: string;
    children?: ReactNode;
    gradient?: string | null;
}

// const DEFAULT_GRADIENT = 'linear-gradient(90deg, rgba(255, 255, 255, 0) 10%, rgba(255, 255, 255, 0) 30%, #FFFFFF 80%)';

export const HorizontalScrollContainer = (props: IHorizontalScrollContainerProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const {children, className, gradient} = props;
    const [hasLeftOffset, setHasLeftOffset] = useState(false);
    const [hasRightOffset, setHasRightOffset] = useState(false);

    const hasGradient = Boolean(gradient);

    const detectOverflow = useCallback((e: Element) => {
        setHasLeftOffset(Boolean(Math.floor(e.scrollLeft)));
        setHasRightOffset(Math.floor(e.scrollWidth - e.clientWidth) > Math.floor(e.scrollLeft) + 1);
    }, []);

    useEffect(() => {
        const observer: ResizeObserver = new ResizeObserver(
            _throttle((entries) => {
                const [current] = entries;
                if (current) {
                    detectOverflow(current.target);
                }
            }, 100),
        );

        if (ref.current) {
            detectOverflow(ref.current);
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, [detectOverflow]);

    useEffect(() => {
        const el = ref.current;
        if (!el) {
            return;
        }

        const onWheel = (e: WheelEvent) => {
            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                return;
            }

            const canScrollLeft = 0 < el.scrollLeft;
            const canScrollRight = Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth;

            const scrollingLeft = 0 > e.deltaY;
            const scrollingRight = 0 < e.deltaY;

            if ((scrollingLeft && canScrollLeft) || (scrollingRight && canScrollRight)) {
                e.preventDefault();
                el.scrollLeft += e.deltaY;
            }
        };

        el.addEventListener('wheel', onWheel, {passive: false});
        // eslint-disable-next-line consistent-return
        return () => el.removeEventListener('wheel', onWheel);
    }, []);

    const handleScroll = (e: UIEvent<HTMLElement>) => {
        detectOverflow(e.currentTarget);
    };

    return (
        <div className={styles.outer}>
            {hasGradient && (
                <div
                    style={{background: gradient!}}
                    className={cs(styles.leftFade, {[styles.visible]: hasLeftOffset})}
                />
            )}
            <div ref={ref} className={cs(className, styles.main)} onScroll={handleScroll}>
                {children}
            </div>
            {hasGradient && (
                <div
                    style={{background: gradient!}}
                    className={cs(styles.rightFade, {[styles.visible]: hasRightOffset})}
                />
            )}
        </div>
    );
};
